import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { Pencil } from "lucide-react";

interface EditDormitoryMotherInlineProps {
  dormitory: string;
  currentMother: string;
  currentContact: string;
}

export const EditDormitoryMotherInline = ({
  dormitory,
  currentMother,
  currentContact,
}: EditDormitoryMotherInlineProps) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [motherName, setMotherName] = useState(currentMother);
  const [motherContact, setMotherContact] = useState(currentContact);

  const updateDormitoryMother = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("students")
        .update({
          dormitory_mother: motherName,
          dormitory_mother_contact: motherContact,
        })
        .eq("school_id", user?.schoolId)
        .eq("dormitory", dormitory);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Dormitory mother updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["boarding-student-visits-details"] });
      queryClient.invalidateQueries({ queryKey: ["todays-dorm-mothers"] });
      setIsOpen(false);
    },
    onError: (error) => {
      console.error("Error updating dormitory mother:", error);
      toast({
        title: "Error",
        description: "Failed to update dormitory mother",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateDormitoryMother.mutate();
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="h-6 px-2"
      >
        <Pencil className="h-3 w-3" />
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Edit Dormitory Mother</DialogTitle>
              <DialogDescription>
                Update the dormitory mother details for {dormitory}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="mother-name">Mother's Name</Label>
                <Input
                  id="mother-name"
                  value={motherName}
                  onChange={(e) => setMotherName(e.target.value)}
                  placeholder="Enter mother's name"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="mother-contact">Contact Number</Label>
                <Input
                  id="mother-contact"
                  value={motherContact}
                  onChange={(e) => setMotherContact(e.target.value)}
                  placeholder="Enter contact number"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateDormitoryMother.isPending}
              >
                {updateDormitoryMother.isPending ? "Updating..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
